import { useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { BEFORE_AFTER_GALLERY } from "@/data/site";
import { Reveal } from "@/components/common/Reveal";

/**
 * Reusable Before & After gallery ("short code").
 * Drop <BeforeAfterGallery /> on any page.
 * Props:
 *  - limit: how many images to show before "View all" (default 6)
 *  - muted: use soft background instead of white
 *  - eyebrow / title / subtitle: override copy
 */
export const BeforeAfterGallery = ({
  limit = 6,
  muted = false,
  eyebrow = "Before & After",
  title = "Real patient transformations",
  subtitle = "Genuine results from Dr. Ankesh Seth. Every image shows the same patient before and after treatment.",
}) => {
  const [showAll, setShowAll] = useState(false);
  const [active, setActive] = useState(null);

  const items = BEFORE_AFTER_GALLERY;
  const visible = showAll ? items : items.slice(0, limit);

  const openAt = (idx) => setActive(idx);
  const close = () => setActive(null);
  const prev = () => setActive((a) => (a - 1 + items.length) % items.length);
  const next = () => setActive((a) => (a + 1) % items.length);

  return (
    <section
      data-testid="before-after-gallery"
      className={muted ? "bg-brand-soft" : "bg-white"}
    >
      <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-12 md:py-32 lg:px-20">
        <Reveal className="mb-12 max-w-3xl">
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-blue">
            {eyebrow}
          </span>
          <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.02] tracking-tight text-brand-dark md:text-5xl lg:text-6xl">
            {title.split(" ").slice(0, -1).join(" ")}{" "}
            <span className="italic text-brand-blue">{title.split(" ").slice(-1)}</span>
          </h2>
          <p className="mt-5 text-brand-muted">{subtitle}</p>
        </Reveal>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((img, idx) => (
            <Reveal key={img.id} delay={(idx % 3) * 0.06}>
              <button
                type="button"
                data-testid={`before-after-image-${img.id}`}
                onClick={() => openAt(items.findIndex((x) => x.id === img.id))}
                className="group relative block w-full overflow-hidden rounded-2xl border border-brand-line bg-brand-soft"
              >
                <div className="aspect-[5/4] w-full overflow-hidden">
                  <img
                    src={img.src}
                    alt={`Patient before and after result ${img.id}`}
                    loading="lazy"
                    width="500"
                    height="400"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <span className="pointer-events-none absolute inset-0 bg-brand-accent/0 transition-colors duration-300 group-hover:bg-brand-accent/10" />
              </button>
            </Reveal>
          ))}
        </div>

        {items.length > limit && (
          <div className="mt-10 flex justify-center">
            <button
              type="button"
              data-testid="before-after-toggle"
              onClick={() => setShowAll((s) => !s)}
              className="rounded-full border border-brand-blue px-7 py-3 text-sm font-semibold text-brand-blue transition-all hover:bg-brand-blue hover:text-white active:scale-95"
            >
              {showAll ? "Show less" : `View all ${items.length} results`}
            </button>
          </div>
        )}
      </div>

      {/* Lightbox */}
      <Dialog open={active !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent
          className="max-w-3xl border-none bg-transparent p-0 shadow-none"
          data-testid="before-after-lightbox"
        >
          <DialogTitle className="sr-only">Before and after result</DialogTitle>
          {active !== null && (
            <div className="relative">
              <img
                src={items[active].src}
                alt={`Patient before and after result ${items[active].id}`}
                className="w-full rounded-2xl"
              />
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                className="absolute -right-2 -top-12 flex h-10 w-10 items-center justify-center rounded-full bg-white text-brand-dark shadow-lg md:-right-12 md:top-0"
              >
                <X className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={prev}
                aria-label="Previous"
                className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-brand-dark shadow-lg transition hover:bg-white"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Next"
                className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-brand-dark shadow-lg transition hover:bg-white"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
